import { useState, useEffect, useMemo } from "react";
import { getMintSpaceMembers } from "../api/mintSpace";
import { useSocket } from "../context/SocketContext";
import { useTranslation } from "../context/TranslationContext";
import Button from "./ds/Button";
import Icon from "./ds/Icon";
import UserProfileLink from "./UserProfileLink";

// Treat a user as online if their lastSeen is within this window (matches socket
// heartbeat: lastSeen is refreshed on connect and disconnect).
const ONLINE_WINDOW_MS = 2 * 60 * 1000;

/**
 * Figma "People": who's behind the campaign. `action` sits under the title and
 * `children` above the invite button.
 */
const CampaignPeopleCard = ({ campaign, mintSpaceId: mintSpaceIdProp, setToast, onSeeAll, action = null, children = null }) => {
  const { t } = useTranslation();
  const { socket } = useSocket();
  const [owner, setOwner] = useState(null);
  const [moderators, setModerators] = useState([]);
  const [members, setMembers] = useState([]);
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());

  const mintSpaceId = useMemo(() => {
    if (mintSpaceIdProp) return mintSpaceIdProp;
    const ms = campaign?.mintSpace;
    if (!ms) return null;
    return typeof ms === "object" ? ms._id : ms;
  }, [mintSpaceIdProp, campaign?.mintSpace]);

  useEffect(() => {
    if (!mintSpaceId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getMintSpaceMembers(mintSpaceId);
        if (cancelled || !response.success) return;
        const {
          owner: o,
          moderators: mods = [],
          members: mems = [],
        } = response.data;
        setOwner(o || null);
        setModerators(mods);
        setMembers(mems);

        const initialOnline = new Set();
        const now = Date.now();
        [o, ...mods, ...mems].forEach((u) => {
          if (!u?._id || !u.lastSeen) return;
          if (now - new Date(u.lastSeen).getTime() <= ONLINE_WINDOW_MS) {
            initialOnline.add(String(u._id));
          }
        });
        setOnlineUserIds(initialOnline);
      } catch (error) {
        console.error("Error fetching campaign people:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mintSpaceId]);

  useEffect(() => {
    if (!socket) return;
    const onOnline = ({ userId }) => {
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        next.add(String(userId));
        return next;
      });
    };
    const onOffline = ({ userId }) => {
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        next.delete(String(userId));
        return next;
      });
    };
    socket.on("user_online", onOnline);
    socket.on("user_offline", onOffline);
    return () => {
      socket.off("user_online", onOnline);
      socket.off("user_offline", onOffline);
    };
  }, [socket]);

  const getDisplayName = (u) => u?.businessName || u?.username || "User";
  const isOnline = (id) => id && onlineUserIds.has(String(id));

  const teamMembers = useMemo(() => {
    const items = [];
    if (owner) items.push({ user: owner, role: "owner" });
    moderators.forEach((m) => {
      if (!owner || String(m._id) !== String(owner._id)) {
        items.push({ user: m, role: "mod" });
      }
    });
    return items;
  }, [owner, moderators]);

  const totalPeople = useMemo(() => {
    const ids = new Set();
    if (owner?._id) ids.add(String(owner._id));
    moderators.forEach((m) => m?._id && ids.add(String(m._id)));
    members.forEach((m) => m?._id && ids.add(String(m._id)));
    return ids.size;
  }, [owner, moderators, members]);

  const handleCopyInvite = async () => {
    if (!mintSpaceId) return;
    const url = `${window.location.origin}/mint-space/${mintSpaceId}`;
    try {
      await navigator.clipboard.writeText(url);
      if (setToast)
        setToast(t("campaigns.inviteLinkCopied"));
    } catch {
      if (setToast) setToast(t("common.copyFailed"));
    }
  };

  if (!mintSpaceId) return null;

  return (
    <section className="flex flex-col gap-space-12 rounded-radius-lg bg-bg-raised p-space-16">
      <div className="flex items-center justify-between gap-space-8">
        <h3 className="text-headline text-label-primary">
          {t("campaigns.people")} · {totalPeople}
        </h3>
        {onSeeAll && (
          <Button variant="plain" size="s" onClick={onSeeAll}>
            {t("common.seeAll")}
          </Button>
        )}
      </div>

      {action}

      {teamMembers.length > 0 && (
        <div className="flex flex-col gap-space-12">
          <p className="text-caps-label uppercase text-label-secondary">{t("campaigns.team")}</p>
          <ul className="flex flex-col gap-space-12">
            {teamMembers.map(({ user, role }) => (
              <li key={user._id} className="flex min-w-0 items-center gap-space-12">
                <div className="relative shrink-0">
                  <UserProfileLink user={user} avatarSize="md" showAvatar nameClassName="sr-only" fallback={getDisplayName(user)} />
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 size-[10px] rounded-full ring-2 ring-bg-raised ${
                      isOnline(user._id) ? "bg-status-success" : "bg-label-tertiary"
                    }`}
                    title={isOnline(user._id) ? t("common.online") : t("common.offline")}
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <UserProfileLink
                    user={user}
                    showAvatar={false}
                    nameClassName="truncate text-subheadline-semibold text-label-primary"
                    fallback="User"
                  />
                  <p className="truncate text-footnote text-label-secondary">
                    {user?.username ? `@${user.username} · ` : ""}
                    {role === "owner" ? t("campaigns.staff") : t("campaigns.mod")}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {children}

      <Button
        variant="tinted"
        size="m"
        fullWidth
        icon={<Icon name="link" size={20} />}
        onClick={handleCopyInvite}
      >
        {t("campaigns.copyInviteLink")}
      </Button>
    </section>
  );
};

export default CampaignPeopleCard;
